import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';
import type { CountryCode } from './country.js';

/**
 * F31 — Skill-Based Matching Engine.
 *
 * Decisions: see plans/31_F31_skill_based_matching.md (Decisions 1–17).
 *
 *   D1  Tiered-K ELO (K=40 calibration / K=20 standard / K=12 stable; ±0.3 swing cap)
 *   D2  Hybrid outcome validation (1v1 both-confirm; team captain + 24h dispute)
 *   D3  Admin-authored sport_skill_descriptors + seeded defaults
 *   D4  Win/loss + optional score + sport-aware draws (SPORT_SUPPORTS_DRAWS gate)
 *   D5  24h dispute window auto-confirm
 *   D6  Ratable-booking 5-gate check
 *   D7  Required new-sport self-rating with inline level descriptions
 *   D8/9 Calibration window + minimum-N gate (matches_played >= 5)
 *   D10 Inactivity = freeze (no decay)
 *   D11 'provisional' coarse pill until minimum-N met
 *   D14 Anti-gaming: per-match swing cap + daily throttle + min-time-between
 *   D15 Admin rating override (super_admin only, mandatory reason ≥10 chars)
 *   D16 booking_participants snapshot at completion-time
 *   D17 Sport remove + re-add → calibration K resets
 *
 * Cross-bundler note: imports use `.js` extensions per the F03 cross-bundler
 * seam (consumed by both NodeNext API and Bundler/Metro web+mobile chains).
 */

// ─── Tiered-K ELO constants (Decision 1 / 8 / 9 / 14) ───────────────────────

/**
 * Rescale 1.0–7.0 → ELO-space. A 1.0 unit gap on the BraveMove scale = 100 ELO
 * points ≈ 64% win probability for the higher player.
 *
 * The scale factor is deliberately tighter than the traditional 200-or-400-per-
 * skill-step ELO conventions because BraveMove stores `numeric(2,1)` (0.1 precision)
 * AND the K schedule maxes out at 40. With a 200-point scale + K=20, the raw
 * delta for an even-match upset is 20 × 0.5 / 200 = 0.05 BraveMove which then
 * rounds to 0.0 — meaning ratings would only move during the calibration
 * window. Tightening to 100 makes K=20 matches actually nudge ratings (0.1
 * BraveMove per coin flip) without making 1.0 gaps feel like a sure thing.
 */
export const ELO_SCALE_FACTOR = 100;
export const ELO_BASE_OFFSET = 400;

/** K-factor schedule (Decision 1). */
export const ELO_K_CALIBRATION = 40;
export const ELO_K_STANDARD = 20;
export const ELO_K_STABLE = 12;

/** Per-match hard swing cap on the 1.0–7.0 scale (Decision 14). */
export const ELO_SWING_CAP = 0.3;

/** Bounds on the public scale. */
export const ELO_MIN_RATING = 1.0;
export const ELO_MAX_RATING = 7.0;

/** Calibration-window length in matches (Decision 8). matches_played < 10 → K=40. */
export const CALIBRATION_THRESHOLD_MATCHES = 10;

/** Stable-zone threshold (Decision 1). matches_played >= 50 → K=12. */
export const STABLE_THRESHOLD_MATCHES = 50;

/**
 * Minimum confirmed matches before public profile shows the numeric rating
 * (Decision 9 / 11). Below this threshold, public viewers see a 'provisional'
 * coarse pill instead of the number.
 */
export const MINIMUM_MATCHES_FOR_CONFIRMED_RATING = 5;

/** 1v1 outcome auto-expiry (Decision 2). One unconfirmed → expire after 7d. */
export const MATCH_OUTCOME_EXPIRY_DAYS_1V1 = 7;

/** Team-match dispute window (Decision 5). Silence → auto-confirm at hour 25. */
export const MATCH_OUTCOME_DISPUTE_WINDOW_HOURS = 24;

/**
 * Anti-gaming: max ratable matches per (user × sport) per UTC day (Decision 14).
 * 6th+ outcomes written but flagged `result_applied=false` w/ DAILY_THROTTLE.
 */
export const RATING_MAX_DAILY_MATCHES_PER_SPORT = 5;

/** Anti-gaming: rapid-fire guard — minimum gap in minutes between two outcomes' start_at. */
export const RATING_RAPID_FIRE_MIN_GAP_MINUTES = 30;

// ─── EloResult (Decision 4) ─────────────────────────────────────────────────

/**
 * Per-side outcome used by the ELO math. `'draw'` permitted only when the
 * sport's SPORT_SUPPORTS_DRAWS flag is true; the service layer validates.
 */
export type EloResult = 'win' | 'loss' | 'draw';

export const EloResultSchema = z.enum(['win', 'loss', 'draw']);

/** Per-match shape on `match_outcomes` (Decision 4). Side a = host_user_id's side. */
export const WINNING_SIDE_VALUES = ['a', 'b', 'draw'] as const;
export type WinningSide = (typeof WINNING_SIDE_VALUES)[number];
export const WinningSideSchema = z.enum(WINNING_SIDE_VALUES);

// ─── MatchOutcomeStatus (Decision 2) ────────────────────────────────────────

export const MATCH_OUTCOME_STATUSES = [
  'pending_confirmation',
  'confirmed',
  'disputed',
  'resolved_by_admin',
  'expired',
] as const;
export type MatchOutcomeStatus = (typeof MATCH_OUTCOME_STATUSES)[number];
export const MatchOutcomeStatusSchema = z.enum(MATCH_OUTCOME_STATUSES);

// ─── ReportedByRole (Decision 2) ────────────────────────────────────────────

export const REPORTED_BY_ROLES = ['participant', 'captain', 'admin'] as const;
export type ReportedByRole = (typeof REPORTED_BY_ROLES)[number];
export const ReportedByRoleSchema = z.enum(REPORTED_BY_ROLES);

// ─── MatchOutcomeSource (F41 / 0036 M12) ────────────────────────────────────

/**
 * Which roster path produced this outcome (migration 0036):
 *   - 'booking'    — the F31 default. Roster comes from `booking_participants`
 *                    + `bookings` (host/team). `booking_id` is set, byte-for-byte.
 *   - 'tournament' — the M12 path. Roster comes from `tournament_match_participants`
 *                    (the side column is taken directly). `tournament_match_id`
 *                    is set; `booking_id` is NULL. Throttle-exempt (organizer/
 *                    admin-verified outcomes, D16).
 * Exactly one of (booking_id, tournament_match_id) is set, consistent with this.
 */
export const MATCH_OUTCOME_SOURCES = ['booking', 'tournament'] as const;
export type MatchOutcomeSource = (typeof MATCH_OUTCOME_SOURCES)[number];
export const MatchOutcomeSourceSchema = z.enum(MATCH_OUTCOME_SOURCES);

// ─── BookingParticipantRole (Decision 16) ───────────────────────────────────

export const BOOKING_PARTICIPANT_ROLES = ['booker', 'team_member', 'guest'] as const;
export type BookingParticipantRole = (typeof BOOKING_PARTICIPANT_ROLES)[number];
export const BookingParticipantRoleSchema = z.enum(BOOKING_PARTICIPANT_ROLES);

// ─── MatchConfirmationStatus (Decision 2) ───────────────────────────────────

export const MATCH_CONFIRMATION_STATUSES = ['pending', 'confirmed', 'disputed'] as const;
export type MatchConfirmationStatus = (typeof MATCH_CONFIRMATION_STATUSES)[number];

// ─── RatingHistoryKind ──────────────────────────────────────────────────────

export const RATING_HISTORY_KINDS = [
  'match_engine',
  'admin_override',
  'self_set',
  'calibration_reset',
] as const;
export type RatingHistoryKind = (typeof RATING_HISTORY_KINDS)[number];
export const RatingHistoryKindSchema = z.enum(RATING_HISTORY_KINDS);

// ─── AntiGamingSkipReason (Decision 14) ─────────────────────────────────────

export const ANTI_GAMING_SKIP_REASONS = ['DAILY_THROTTLE', 'RAPID_FIRE'] as const;
export type AntiGamingSkipReason = (typeof ANTI_GAMING_SKIP_REASONS)[number];

// ─── ConfidenceTier (Decision 11) ───────────────────────────────────────────

/**
 * Surface flag derived from `matches_played` vs `MINIMUM_MATCHES_FOR_CONFIRMED_RATING`.
 * Driven by F08's privacy-projection extension (Wave 2A). Drives the F02
 * SkillLevelBadge coarse-pill render path (Wave 1.2 extension).
 */
export const CONFIDENCE_TIERS = ['provisional', 'confirmed'] as const;
export type ConfidenceTier = (typeof CONFIDENCE_TIERS)[number];

// ─── SkillErrorCode ─────────────────────────────────────────────────────────

export const SKILL_ERROR_CODES = [
  'INVALID_DRAW',
  'NOT_RATABLE_BOOKING',
  'OUTCOME_ALREADY_CONFIRMED',
  'OUTCOME_EXPIRED',
  'NOT_PARTICIPANT',
  'NOT_CAPTAIN',
  'RAPID_FIRE_REJECTED',
  'DAILY_THROTTLE_REJECTED',
  'DISPUTE_WINDOW_CLOSED',
  'OVERRIDE_REASON_REQUIRED',
] as const;
export type SkillErrorCode = (typeof SKILL_ERROR_CODES)[number];

// ─── Input schemas (Section 7 of F31 plan) ──────────────────────────────────

/**
 * 1v1 outcome confirmation. Caller asserts whether they won, lost, or drew (if
 * the sport supports draws). Score is optional per Decision 4.
 */
export const ConfirmMatchOutcomeInputSchema = z
  .object({
    result: EloResultSchema,
    scoreA: z.number().int().nonnegative().optional(),
    scoreB: z.number().int().nonnegative().optional(),
    scoreNotes: z.string().max(280).optional(),
  })
  .strict();
export type ConfirmMatchOutcomeInput = z.infer<typeof ConfirmMatchOutcomeInputSchema>;

/**
 * Team-match captain report. winningSide expressed in per-match terms ('a' =
 * host side / 'b' = opposing side / 'draw' if sport supports draws).
 */
export const CaptainReportOutcomeInputSchema = z
  .object({
    winningSide: WinningSideSchema,
    scoreA: z.number().int().nonnegative().optional(),
    scoreB: z.number().int().nonnegative().optional(),
    scoreNotes: z.string().max(280).optional(),
  })
  .strict();
export type CaptainReportOutcomeInput = z.infer<typeof CaptainReportOutcomeInputSchema>;

/**
 * Dispute opened by a non-captain participant within the 24h window. Mandatory
 * reason ≥10 chars.
 */
export const DisputeMatchOutcomeInputSchema = z
  .object({
    reason: z.string().min(10).max(500),
  })
  .strict();
export type DisputeMatchOutcomeInput = z.infer<typeof DisputeMatchOutcomeInputSchema>;

/**
 * Captain marks a participant present or absent before reporting. Absent
 * participants don't receive a rating delta when the outcome is applied.
 */
export const MarkParticipantPresenceInputSchema = z
  .object({
    userId: z.string().uuid(),
    present: z.boolean(),
  })
  .strict();
export type MarkParticipantPresenceInput = z.infer<typeof MarkParticipantPresenceInputSchema>;

/**
 * Admin dispute resolution. Picks a final winning side + mandatory reason.
 */
export const ResolveMatchDisputeInputSchema = z
  .object({
    winningSide: WinningSideSchema,
    reason: z.string().min(10).max(500),
  })
  .strict();
export type ResolveMatchDisputeInput = z.infer<typeof ResolveMatchDisputeInputSchema>;

/**
 * Admin rating override (Decision 15). Mandatory reason ≥10 chars. The override
 * can set values above 5.0 (unlike the user-side cap).
 */
export const AdminRatingOverrideInputSchema = z
  .object({
    newEffectiveSkillValue: z
      .number()
      .min(ELO_MIN_RATING)
      .max(ELO_MAX_RATING)
      .refine((v) => Number.isFinite(v) && Math.round(v * 10) === v * 10, {
        message: 'newEffectiveSkillValue must be a value with at most one decimal place',
      }),
    reason: z.string().min(10).max(500),
  })
  .strict();
export type AdminRatingOverrideInput = z.infer<typeof AdminRatingOverrideInputSchema>;

/**
 * Admin CMS — upsert a (sport × level band) descriptor. Wave 2B's
 * `/admin/sport-skill-descriptors` page consumes this.
 */
export const UpsertSportSkillDescriptorInputSchema = z
  .object({
    sportName: SportNameSchema,
    levelBandMin: z
      .number()
      .min(ELO_MIN_RATING)
      .max(ELO_MAX_RATING)
      .refine((v) => Math.round(v * 10) === v * 10, {
        message: 'levelBandMin must have at most one decimal place',
      }),
    levelBandMax: z
      .number()
      .min(ELO_MIN_RATING)
      .max(ELO_MAX_RATING)
      .refine((v) => Math.round(v * 10) === v * 10, {
        message: 'levelBandMax must have at most one decimal place',
      }),
    bodyText: z.string().min(20).max(2000),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.levelBandMax <= val.levelBandMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'levelBandMax must be greater than levelBandMin',
        path: ['levelBandMax'],
      });
    }
  });
export type UpsertSportSkillDescriptorInput = z.infer<typeof UpsertSportSkillDescriptorInputSchema>;

/**
 * Cursor-paginated query for `/me/match-outcomes`.
 */
export const ListMatchOutcomesQuerySchema = z
  .object({
    sport: SportNameSchema.optional(),
    status: MatchOutcomeStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
    cursor: z.string().min(1).optional(),
  })
  .strict();
export type ListMatchOutcomesQuery = z.infer<typeof ListMatchOutcomesQuerySchema>;

// ─── Entity read shapes ─────────────────────────────────────────────────────

/**
 * One row in `match_outcomes`. Returned by `GET /me/match-outcomes/:id` for
 * participants + the admin dispute-detail screen.
 */
export interface MatchOutcome {
  readonly id: string;
  // F41 (0036, M12) — nullable: a tournament-sourced outcome carries
  // `tournamentMatchId` instead. Exactly one of (bookingId, tournamentMatchId)
  // is set, consistent with `source`.
  readonly bookingId: string | null;
  readonly source: MatchOutcomeSource;
  readonly tournamentMatchId: string | null;
  readonly sport: SportName;
  readonly status: MatchOutcomeStatus;
  readonly reportedByUserId: string | null;
  readonly reportedByRole: ReportedByRole;
  readonly reportedAt: string;
  readonly winningSide: WinningSide | null;
  readonly scoreA: number | null;
  readonly scoreB: number | null;
  readonly scoreNotes: string | null;
  readonly confirmedAt: string | null;
  readonly disputedAt: string | null;
  readonly disputedByUserId: string | null;
  readonly disputeReason: string | null;
  readonly resolvedAt: string | null;
  readonly resolvedByAdminId: string | null;
  readonly adminResolutionReason: string | null;
  readonly expiresAt: string;
  readonly resultApplied: boolean;
  readonly resultAppliedAt: string | null;
  readonly antiGamingSkipReason: AntiGamingSkipReason | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Full detail shape returned by `GET /me/match-outcomes/:id`. */
export interface MatchOutcomeDetail extends MatchOutcome {
  readonly participants: readonly BookingParticipant[];
  readonly confirmations: readonly MatchOutcomeConfirmation[];
  readonly ratingDeltas: readonly MatchRatingDelta[];
}

/** Compact row in the user's match-outcome history list. */
export interface MatchOutcomeListItem {
  readonly id: string;
  // F41 (0036, M12) — nullable for tournament-sourced outcomes (see MatchOutcome).
  readonly bookingId: string | null;
  readonly sport: SportName;
  readonly status: MatchOutcomeStatus;
  readonly winningSide: WinningSide | null;
  readonly result: EloResult | null;
  readonly deltaForViewer: number | null;
  readonly resultApplied: boolean;
  readonly reportedAt: string;
  readonly confirmedAt: string | null;
}

/** One row in `booking_participants` (Decision 16). */
export interface BookingParticipant {
  readonly bookingId: string;
  readonly userId: string;
  readonly role: BookingParticipantRole;
  readonly present: boolean;
  readonly wasTeamId: string | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** One row in `match_outcome_confirmations`. */
export interface MatchOutcomeConfirmation {
  readonly outcomeId: string;
  readonly userId: string;
  readonly status: MatchConfirmationStatus;
  readonly confirmedAt: string | null;
  readonly disputedAt: string | null;
  readonly disputeReason: string | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
}

/** One row in `rating_history`. */
export interface RatingHistoryEntry {
  readonly id: string;
  readonly userId: string;
  readonly sport: SportName;
  readonly kind: RatingHistoryKind;
  readonly previousValue: number;
  readonly newValue: number;
  readonly delta: number;
  readonly kFactor: number | null;
  readonly matchOutcomeId: string | null;
  readonly actorAdminId: string | null;
  readonly reason: string | null;
  readonly matchesPlayedAtEvent: number;
  readonly countryCode: CountryCode;
  readonly appliedAt: string;
}

/** Per-participant delta produced by applying an outcome. */
export interface MatchRatingDelta {
  readonly userId: string;
  readonly sport: SportName;
  readonly previousValue: number;
  readonly newValue: number;
  readonly delta: number;
  readonly kFactor: number;
  readonly resultApplied: boolean;
}

/** One row in `sport_skill_descriptors` (Decision 3). */
export interface SportSkillDescriptor {
  readonly id: string;
  readonly sportName: SportName;
  readonly levelBandMin: number;
  readonly levelBandMax: number;
  readonly bodyText: string;
  readonly countryCode: CountryCode;
  readonly updatedByAdminId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Per-sport rating summary returned by `GET /me/skill-ratings`. */
export interface SkillRatingSummary {
  readonly sport: SportName;
  readonly claimedSkillValue: number;
  readonly effectiveSkillValue: number;
  readonly skillSource: 'self' | 'match_engine' | 'admin_verified';
  readonly matchesPlayed: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
  readonly confidenceTier: ConfidenceTier;
  readonly lastRatedMatchAt: string | null;
}

// ─── F31 NotificationEvent payload shapes (consumed by orchestrator) ───────

/** Recipient row reused across the 6 F31 notification events. */
export interface F31NotificationRecipient {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string;
  readonly locale: string;
}

/** Booking context attached to every match-outcome notification. */
export interface F31NotificationBookingContext {
  readonly bookingId: string;
  readonly sportName: SportName;
  readonly startAt: string;
  readonly endAt: string;
  readonly venueName: string;
  readonly subVenueName: string | null;
}
